import _ from 'lodash';
import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {
  CrmSyncAccountDefaultFieldIdsEnum,
  CrmSyncMappingCustomFieldTypesEnum,
} from '../../../../../models/api/crmSyncModel';
import {
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
  CustomFieldsEnum,
} from '../../../../../models/api/customFieldModel';
import {AppState} from '../../../../../store/appState';
import {getFlatCustomFields} from '../../../../../store/data/dataSelectors';
import {defaultCrmCustomFields} from '../crmSyncHelpers';
import {CrmCustomFieldDropdownWrapper} from './crmCustomFieldDropdownWrapper';

/*
 * Interface.
 */

interface CrmCustomFieldDropdownContainerProps {
  types: ReadonlyArray<CustomFieldsEnum | CrmSyncMappingCustomFieldTypesEnum>;
  selectedCustomFieldId?: number;
  allSelectedCustomFieldIds: ReadonlyArray<number>;
}

/*
 * Selectors.
 */

function getTypes(_state: AppState, props: CrmCustomFieldDropdownContainerProps) {
  return props.types;
}

function getSelectedCustomFieldId(_state: AppState, props: CrmCustomFieldDropdownContainerProps) {
  return props.selectedCustomFieldId;
}

function getAllSelectedCustomFieldIds(_state: AppState, props: CrmCustomFieldDropdownContainerProps) {
  return props.allSelectedCustomFieldIds;
}

function makeGetAccountCustomFields() {
  return createSelector(
    [getFlatCustomFields, getTypes, getSelectedCustomFieldId, getAllSelectedCustomFieldIds],
    (customFields, types, selectedCustomFieldId, allSelectedCustomFieldIds) =>
      _([...defaultCrmCustomFields, ...customFields])
        .filter((customField) =>
          filterCustomField(customField, types, allSelectedCustomFieldIds, selectedCustomFieldId),
        )
        .sort((field) => (field.id === selectedCustomFieldId ? -1 : 1))
        .value(),
  );
}

/*
 * Helpers.
 */

function filterCustomField(
  customField: CustomFieldModel,
  types: ReadonlyArray<CustomFieldsEnum | CrmSyncMappingCustomFieldTypesEnum>,
  allSelectedCustomFieldIds: ReadonlyArray<number>,
  selectedCustomFieldId?: number,
) {
  // Only allow account custom field types.
  if (customField.resourceType !== CustomFieldResourceTypesEnum.ACCOUNT) {
    return false;
  }

  // If the custom fields is DESCRIPTION and the type doesn't support DESCRIPTION, do not show it.
  if (
    customField.id === CrmSyncAccountDefaultFieldIdsEnum.DESCRIPTION &&
    !types.includes(CrmSyncMappingCustomFieldTypesEnum.DESCRIPTION)
  ) {
    return false;
  }
  // If the custom fields is DOMAINS and the type doesn't support DOMAINS, do not show it.
  if (
    customField.id === CrmSyncAccountDefaultFieldIdsEnum.DOMAINS &&
    !types.includes(CrmSyncMappingCustomFieldTypesEnum.DOMAINS)
  ) {
    return false;
  }
  // If the custom fields is NAME and the type doesn't support NAME, do not show it.
  if (
    customField.id === CrmSyncAccountDefaultFieldIdsEnum.NAME &&
    !types.includes(CrmSyncMappingCustomFieldTypesEnum.NAME)
  ) {
    return false;
  }

  // Make sure the normal custom field type is valid.
  if (customField.id > 0 && !types.includes(customField.type)) {
    return false;
  }

  // If all the typings are valid, only show the selected field and fields that are not selected by other mappings.
  return customField.id === selectedCustomFieldId || !allSelectedCustomFieldIds.includes(customField.id);
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CrmCustomFieldDropdownContainerProps>()(() => ({
  customFields: makeGetAccountCustomFields(),
}));

export const CrmCustomFieldDropdownContainer = connect(mapStateToProps)(CrmCustomFieldDropdownWrapper);
